import React, { useState, useEffect, useMemo } from 'react';
import { getSuffererByID, updateSufferer } from '../../../../controllers/SuffererController';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import ButtonPrimary from '../../../../components/button/ButtonPrimary';
import ButtonSecondary from '../../../../components/button/ButtonSecondary';
import Skeleton from '../../../../components/Skeleton';

const EditModal = ({ t }) => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [gender, setGender] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [nik, setNik] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [address, setAddress] = useState('');
    const [emergencyName, setEmergencyName] = useState('');
    const [emergencyRelation, setEmergencyRelation] = useState('');
    const [emergencyPhoneNumber, setEmergencyPhoneNumber] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const fromDetail = location.state?.fromDetail;
    
    useEffect(() => {
        const fetchSufferer = async () => {
            try {
                const result = await getSuffererByID(id);
                if (!result) {
                    throw new Error(t('sufferer.fetchData.errorMessage'));
                };
                
                setName(result.name);
                setGender(result.gender);
                setBirthDate(result.birth_date);
                setNik(result.nik);
                setEmail(result.email);
                setPhoneNumber(result.phone_number);
                setAddress(result.address);
                setEmergencyName(result.emergency.name);
                setEmergencyRelation(result.emergency.relation);
                setEmergencyPhoneNumber(result.emergency.phone_number);
            } catch (error) {
                toast.error(error.message);
                navigate('/sufferer');
            } finally {
                setLoading(false);
            };
        };
        
        fetchSufferer();
    }, [id, t, navigate]);
    
    const isDisabled = useMemo(() => {
        const isPhoneNumberValid = /^[1-9][0-9]{9,14}$/.test(phoneNumber.trim());
        const isEmergencyPhoneNumberValid = /^[1-9][0-9]{9,14}$/.test(emergencyPhoneNumber.trim());
        const isBasicFieldsValid =
            name.trim() &&
            gender.trim() &&
            birthDate.trim() &&
            nik.trim().length === 16 &&
            email.trim() &&
            isPhoneNumberValid &&
            address.trim() &&
            emergencyName.trim() &&
            emergencyRelation.trim() &&
            isEmergencyPhoneNumberValid;
        
        return !(isBasicFieldsValid);
    }, [name, gender, birthDate, nik, email, phoneNumber, address, emergencyName, emergencyRelation, emergencyPhoneNumber]);
    
    if (loading) {
        return (
            <div className='space-y-5'>
                <Skeleton className='flex h-36 md:h-64 w-full rounded-lg' />
            </div>
        ); 
    };
    
    const handleUpdate = async () => {
        try {
            const today = new Date().toISOString().split('T')[0];
            if (birthDate > today) {
                throw new Error(t('common.birthDate.errorMessage'));
            };
            
            const data = {
                name,
                gender,
                birth_date: birthDate,
                nik,
                email,
                phone_number: phoneNumber,
                address,
                emergency: {
                    name: emergencyName,
                    relation: emergencyRelation,
                    phone_number: emergencyPhoneNumber,
                },
            };
            
            await updateSufferer(id, data);
            
            if (fromDetail) {
                navigate(`/sufferer/${id}/detail`);
            } else {
                navigate('/sufferer');
            };
            
            toast.success(t('sufferer.edit.message'));
        } catch (error) {
            toast.error(error.message);
        };
    };
    
    const handleBack = () => {
        if (fromDetail) {
            navigate(`/sufferer/${id}/detail`);
        } else {
            navigate('/sufferer');
        };
    };
    
    return (
        <div className='flex flex-col p-5 rounded-lg bg-white space-y-5'>
            {/* Sufferer Data */}
            <div className='space-y-5'>
                <span className='text-base md:text-lg font-medium'>{t('sufferer.title')}:</span>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.name.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.name.placeholderText')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.gender.title')}:</span>
                    <select
                        className={`
                            w-full px-3 py-2 rounded bg-white border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                    >
                        <option value='' disabled={gender !== ''}>{t('common.gender.placeholderSelect')}</option>
                        <option value='Laki-laki'>{t('common.gender.laki-laki')}</option>
                        <option value='Perempuan'>{t('common.gender.perempuan')}</option>
                    </select>
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.birthDate.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='date'
                        max={new Date().toISOString().split('T')[0]}
                        value={birthDate}
                        onChange={(e) => setBirthDate(e.target.value)}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.nik.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.nik.placeholderText')}
                        value={nik}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d{0,16}$/.test(value)) {
                                setNik(value);
                            };
                        }}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.email.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.email.placeholderText')}
                        value={email}
                        onChange={(e) => {
                            const input = e.target.value.toLowerCase();
                            setEmail(input)
                        }}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.phoneNumber.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.phoneNumber.placeholderText')}
                        value={phoneNumber}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^[1-9][0-9]{0,14}$/.test(value)) {
                                setPhoneNumber(value);
                            };
                        }}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.address.title')}:</span>
                    <textarea
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.address.placeholderText')}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                    />
                </div>
            </div>
            
            {/* Emergency */}
            <div className='space-y-5'>
                <span className='text-base md:text-lg font-medium'>{t('common.emergency.title')}:</span>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.name.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.name.placeholderText')}
                        value={emergencyName}
                        onChange={(e) => setEmergencyName(e.target.value)}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.emergency.relation.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.emergency.relation.placeholderText')}
                        value={emergencyRelation}
                        onChange={(e) => setEmergencyRelation(e.target.value)}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.phoneNumber.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.phoneNumber.placeholderText')}
                        value={emergencyPhoneNumber}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^[1-9][0-9]{0,14}$/.test(value)) {
                                setEmergencyPhoneNumber(value);
                            };
                        }}
                    />
                </div>
            </div>
            <div className='flex justify-center sm:justify-end space-x-5'>
                <ButtonSecondary className='h-full w-24 md:w-32 md:text-sm' onClick={handleBack}>{t('common.action.back')}</ButtonSecondary>
                <ButtonPrimary className='h-full w-24 md:w-32 md:text-sm' isDisabled={isDisabled} onClick={handleUpdate}>{t('common.action.edit')}</ButtonPrimary>
            </div>
        </div>
    );
};

export default EditModal;
